import { useState, useEffect } from 'react';
import { Input, Button, Space, Row, Col, Typography, Select, Divider, Tag } from 'antd';
import { CalendarOutlined, CheckOutlined, ClearOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { CountryFlag } from '../../components/Common/CountryFlag';
import { useCommonParams } from './CommonParamsContext';
import eventsService, { type EventWithCircuit } from '../../api/eventsService';
import { SeasonSelector } from '../../components/Common/SeasonSelector';
import { formatDateRange } from '../../utils/dateUtils';
import './CommonParamsInput.css';

const { Text } = Typography;
const { Option } = Select;

export const CommonParamsInput = () => {
  const {
    seasonYear,
    circuitId,
    startDate,
    source,
    setSeasonYear,
    setCircuitId,
    setStartDate,
    setSource,
    clearAll,
  } = useCommonParams();

  const [seasonYearInput, setSeasonYearInput] = useState(seasonYear || '');
  const [circuitIdInput, setCircuitIdInput] = useState(circuitId || '');
  const [startDateInput, setStartDateInput] = useState(startDate || '');
  const [sourceInput, setSourceInput] = useState(source || '');

  // Event picker state
  const [pickerSeason, setPickerSeason] = useState<number | undefined>();
  const [pickerEvents, setPickerEvents] = useState<EventWithCircuit[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    if (!pickerSeason) return;
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const response = await eventsService.getAllEvents(pickerSeason);
        if (response.success && response.data) {
          setPickerEvents(response.data);
        }
      } catch {
        // silently ignore
      } finally {
        setEventsLoading(false);
      }
    };
    fetchEvents();
  }, [pickerSeason]);

  const makeEventKey = (e: EventWithCircuit) =>
    `${e.season_year}::${e.circuit_id}::${e.start_date}`;


  const eventOptionLabel = (e: EventWithCircuit) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <CountryFlag
        country={e.circuit.country}
        style={{ width: 18, height: 13, objectFit: 'cover' }}
      />
      <span>{e.circuit.display_name}</span>
      {e.status === 'Current' && (
        <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
          Active
        </Tag>
      )}
      {e.demo && (
        <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
          Demo
        </Tag>
      )}
      {e.legacy && (
        <Tag color="default" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
          Legacy
        </Tag>
      )}
      <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>
        {formatDateRange(e.start_date, e.end_date)}
      </span>
    </div>
  );

  const handleEventPick = (key: string) => {
    const event = pickerEvents.find((e) => makeEventKey(e) === key);
    if (!event) return;

    const derivedSource = event.legacy ? 'legacy' : event.demo ? 'demo' : 'prod';
    const year = String(event.season_year);
    const date = event.start_date;

    setSeasonYearInput(year);
    setCircuitIdInput(event.circuit_id);
    setStartDateInput(date);
    setSourceInput(derivedSource);

    // Auto-save immediately
    setSeasonYear(year);
    setCircuitId(event.circuit_id);
    setStartDate(date);
    setSource(derivedSource);
  };

  const hasAnyValue = seasonYear || circuitId || startDate || source;
  const hasChanges =
    seasonYearInput !== (seasonYear || '') ||
    circuitIdInput !== (circuitId || '') ||
    startDateInput !== (startDate || '') ||
    sourceInput !== (source || '');

  const handleSave = () => {
    setSeasonYear(seasonYearInput.trim() || null);
    setCircuitId(circuitIdInput.trim() || null);
    setStartDate(startDateInput.trim() || null);
    setSource(sourceInput.trim() || null);
  };

  const handleClear = () => {
    setSeasonYearInput('');
    setCircuitIdInput('');
    setStartDateInput('');
    setSourceInput('');
    clearAll();
  };

  const handleReset = () => {
    setSeasonYearInput(seasonYear || '');
    setCircuitIdInput(circuitId || '');
    setStartDateInput(startDate || '');
    setSourceInput(source || '');
  };

  return (
    <div className="common-params-container">
      <div className="common-params-header">
        <Space>
          <CalendarOutlined />
          <Text strong>Common Parameters</Text>
          {hasAnyValue && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (These values will be pre-filled in endpoint tests)
            </Text>
          )}
        </Space>
      </div>

      {/* Quick-fill from event picker */}
      <Divider style={{ margin: '4px 0 12px', fontSize: 12 }}>
        <Space size={4}>
          <ThunderboltOutlined />
          <Text type="secondary" style={{ fontSize: 12 }}>Quick fill from event</Text>
        </Space>
      </Divider>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={6}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Season
            </Text>
            <SeasonSelector
              value={pickerSeason}
              onChange={(year) => setPickerSeason(year)}
              style={{ width: '100%' }}
            />
          </div>
        </Col>
        <Col xs={24} sm={18}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Event
            </Text>
            <Select
              placeholder="Pick an event to auto-fill parameters"
              loading={eventsLoading}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const event = pickerEvents.find((e) => makeEventKey(e) === option?.value);
                if (!event) return false;
                const q = input.toLowerCase();
                return (
                  event.circuit.display_name.toLowerCase().includes(q) ||
                  event.name.toLowerCase().includes(q) ||
                  event.circuit_id.toLowerCase().includes(q)
                );
              }}
              onChange={handleEventPick}
              value={undefined}
              options={pickerEvents.map((e) => ({
                label: eventOptionLabel(e),
                value: makeEventKey(e),
              }))}
              disabled={eventsLoading || pickerEvents.length === 0}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={6}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Season Year
            </Text>
            <Input
              placeholder="e.g., 2025"
              value={seasonYearInput}
              onChange={(e) => setSeasonYearInput(e.target.value)}
              onPressEnter={handleSave}
              maxLength={4}
            />
          </div>
        </Col>
        <Col xs={24} sm={6}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Circuit ID
            </Text>
            <Input
              placeholder="e.g., monaco"
              value={circuitIdInput}
              onChange={(e) => setCircuitIdInput(e.target.value)}
              onPressEnter={handleSave}
            />
          </div>
        </Col>
        <Col xs={24} sm={6}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Start Date
            </Text>
            <Input
              placeholder="YYYY-MM-DD"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              onPressEnter={handleSave}
              maxLength={10}
            />
          </div>
        </Col>
        <Col xs={24} sm={6}>
          <div className="param-input-group">
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Source
            </Text>
            <Select
              placeholder="Select source"
              value={sourceInput || undefined}
              onChange={(value) => setSourceInput(value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="prod">prod</Option>
              <Option value="legacy">legacy</Option>
              <Option value="demo">demo</Option>
            </Select>
          </div>
        </Col>
      </Row>

      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSave}
          disabled={!hasChanges}
          size="small"
        >
          Save
        </Button>
        {hasChanges && (
          <Button onClick={handleReset} size="small">
            Reset
          </Button>
        )}
        {hasAnyValue && (
          <Button
            danger
            icon={<ClearOutlined />}
            onClick={handleClear}
            size="small"
          >
            Clear All
          </Button>
        )}
      </Space>

      {hasAnyValue && (
        <div className="common-params-display" style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Current values:{' '}
            {seasonYear && <Text strong>Season: {seasonYear}</Text>}
            {circuitId && (
              <>
                {seasonYear && ' • '}
                <Text strong>Circuit: {circuitId}</Text>
              </>
            )}
            {startDate && (
              <>
                {(seasonYear || circuitId) && ' • '}
                <Text strong>Date: {startDate}</Text>
              </>
            )}
            {source && (
              <>
                {(seasonYear || circuitId || startDate) && ' • '}
                <Text strong>Source: {source}</Text>
              </>
            )}
          </Text>
        </div>
      )}
    </div>
  );
};

